import api from './api';

export interface ExameItem {
  id: number;
  nome: string;
  descricao?: string;
  preco?: number;
}

export interface ExameColaboradorItem {
  id: number;
  exame_id: number;
  exame_nome: string;
  preco?: number;
  clinica_id?: number;
}

export interface VariacaoItem {
  id: number;
  nome: string;
  exames_count: number;
}

export interface ExameClinicoItem {
  id: number;
  nome: string;
  obrigatorio: boolean;
  preco: number;
  laboratorial: boolean;
  variacao_exame_clinico_id: number;
}

export interface PrecoColaborador {
  total: number;
  quantidade: number;
}

export interface PrecoEmpresa {
  total: number;
  quantidade: number;
}

class ExamesService {
  /**
   * Lista exames disponíveis para agendamento
   * Usa endpoint do customer-api
   */
  async getExamesDisponiveis(): Promise<ExameItem[]> {
    const response = await api.get<ExameItem[]>('/exames');
    return response.data;
  }

  /**
   * Lista todas as variações (atividades complementares) disponíveis
   * Usa endpoint do customer-api
   */
  async getVariacoes(): Promise<VariacaoItem[]> {
    const response = await api.get<VariacaoItem[]>('/exames/variacoes');
    return response.data;
  }

  /**
   * Lista exames vinculados a um colaborador
   * Usa endpoint do customer-api
   */
  async getExamesPorColaborador(colaboradorId: number): Promise<ExameColaboradorItem[]> {
    const response = await api.get<ExameColaboradorItem[]>(`/exames/colaborador/${colaboradorId}`);
    return response.data;
  }

  /**
   * Vincula exame a um colaborador
   * Usa endpoint do customer-api
   */
  async vincularExameColaborador(
    colaboradorId: number,
    exameId: number,
    clinicaId?: number
  ): Promise<unknown> {
    const response = await api.post(`/exames/colaborador/${colaboradorId}`, {
      exame_id: exameId,
      clinica_id: clinicaId,
    });
    return response.data;
  }

  /**
   * Desvincula exame de um colaborador
   * Usa endpoint do customer-api
   */
  async desvincularExameColaborador(
    colaboradorId: number,
    exameId: number
  ): Promise<void> {
    await api.delete(`/exames/colaborador/${colaboradorId}/exame/${exameId}`);
  }

  /**
   * Lista exames clínicos disponíveis para uma variação e clínica
   * Usa endpoint do customer-api
   */
  async getExamesClinicosPorVariacaoClinica(
    variacaoId: number,
    clinicaId: number
  ): Promise<ExameClinicoItem[]> {
    const response = await api.get<ExameClinicoItem[]>(
      `/exames/exames-clinicos/variacao/${variacaoId}/clinica/${clinicaId}`
    );
    return response.data;
  }

  /**
   * Vincula/desvincula exame clínico a um colaborador
   * Usa endpoint do customer-api
   */
  async vincularExameClinicoColaborador(
    colaboradorId: number,
    exameClinicoId: number,
    variacaoId: number,
    clinicaId: number | undefined,
    vincular: boolean
  ): Promise<unknown> {
    const response = await api.post(
      `/exames/colaborador/${colaboradorId}/exame-clinico`,
      {
        exame_clinico_id: exameClinicoId,
        variacao_id: variacaoId,
        clinica_id: clinicaId,
        vincular,
      }
    );
    return response.data;
  }

  /**
   * Calcula preço total dos exames de um colaborador
   * Usa endpoint do customer-api
   */
  async calcularPrecoColaborador(colaboradorId: number): Promise<PrecoColaborador> {
    const response = await api.get<PrecoColaborador>(
      `/exames/colaborador/${colaboradorId}/preco`
    );
    return response.data;
  }

  /**
   * Calcula preço total de todos os exames da empresa
   * Usa endpoint do customer-api
   */
  async calcularPrecoTotalEmpresa(): Promise<PrecoEmpresa> {
    const response = await api.get<PrecoEmpresa>('/exames/empresa/preco-total');
    return response.data;
  }

  /**
   * Busca preço de um exame clínico em uma clínica específica (para normalização)
   * Usa endpoint público do landing conforme scheduling
   */
  async getPrecoExameClinica(
    exameClinicoId: number,
    clinicaId: number
  ): Promise<number> {
    const response = await api.get<{ preco: number }>(
      `/exames/exames-clinicos/${exameClinicoId}/preco/clinica/${clinicaId}`
    );
    return response.data.preco;
  }

  /**
   * Busca preço base de um exame clínico (qualquer clínica, usado como fallback)
   */
  async getPrecoBaseExameClinico(exameClinicoId: number): Promise<number> {
    const response = await api.get<{ preco: number }>(
      `/exames/exames-clinicos/${exameClinicoId}/preco-base`
    );
    return response.data.preco;
  }

  /**
   * Normaliza preço de um exame clínico (tenta clínica específica, depois base)
   */
  async normalizarPrecoExame(
    exameClinicoId: number,
    clinicaId?: number
  ): Promise<number> {
    if (clinicaId) {
      try {
        return await this.getPrecoExameClinica(exameClinicoId, clinicaId);
      } catch {
        console.warn('⚠️ Preço da clínica não encontrado, usando preço base');
      }
    }
    return await this.getPrecoBaseExameClinico(exameClinicoId);
  }

  /**
   * Normaliza preços de múltiplos exames clínicos
   */
  async normalizarPrecosExames(
    exames: Array<{ exame_clinico_id: number; clinica_id?: number }>
  ): Promise<Map<number, number>> {
    const precos = new Map<number, number>();
    await Promise.all(
      exames.map(async (exame) => {
        const preco = await this.normalizarPrecoExame(
          exame.exame_clinico_id,
          exame.clinica_id
        );
        precos.set(exame.exame_clinico_id, preco);
      })
    );
    return precos;
  }
}

export const examesService = new ExamesService();

